import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { parseISO, set as setTime } from 'date-fns';
import { formatOccurrence as formatOccurrenceLocal } from '../../utils/dateFormat';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateFnsDatePicker, DateFnsTimePicker } from '../Pickers';
import { useClientEra } from '../../hooks/useClientEra';
import { useCustom, useCustomMutation } from '@refinedev/core';
import {
  defaultDraft,
  draftOfSchedule,
  draftProblem,
  scheduleOfDraft,
  summaryOf,
  WEEKDAYS,
  type ReminderScheduleDraft,
  type ReminderWeekday,
} from './rrule';

/**
 * E01/E05 · THE THERAPIST'S RRULE REMINDER SURFACE (board cc-tools-1, chapter "Thought Testing —
 * Therapist Records Thought (portal, E01)", cells S1 · U1/U2 · V1 · X1/X2 — modelled 2026-09-10,
 * founder-approved).
 *
 * WHY IT EXISTS. `E01 · SetReminderSchedule (portal)` has been a registered slice at buildOrder 28
 * since July and was never built, so the portal kept the pre-RRULE 3-slot `dayReminders` table.
 * The founder found what that costs (R71): a client's weekly rule renders there as a plain time
 * toggle, and a therapist adjusting that slot's time silently REWRITES it to daily, because
 * `scheduleOfDaySlot` only ever emits `FREQ=DAILY`. A frequency destroyed by someone who changed a
 * time, with no warning on either surface.
 *
 * WHAT IT SHOWS. The rule as a SENTENCE ("Mon, Wed, Fri at 09:00"), never a raw RRULE — the model's
 * read models carry the rule itself and the surface renders it, which is how E05's own mobile list
 * works. Editing opens the picker below; switching a row off is an End; a switched-off row keeps its
 * time and can be turned back on, which is a fresh Set of the rule the log still holds (there is no
 * re-enable verb, by design).
 *
 * DATA LAYER. Refine's `useCustom` / `useCustomMutation` against the GraphQL dataProvider — NOT the
 * generated Apollo hooks. The portal has no `ApolloProvider` at its root (the mobile app does), so a
 * generated hook here throws "Could not find \"client\" in the context" and takes the whole page
 * down with it. Every other portal screen speaks `meta: {operation, fields, variables}` for the same
 * reason; caught by opening the page, never by a typecheck.
 *
 * ON BEHALF. Every call names `subject` — the CLIENT. Until 2026-09-10 these operations derived the
 * subject from `ctx.user._id`, which on the portal is the therapist, so they were unreachable from
 * here (T99 (a)). The server authorises with the same E07 grant edge every on-behalf resolver reads,
 * stamps `performer`, and REFUSES a floating rule written for someone else (R70) — so a therapist
 * cannot set a time that has no zone to be read against.
 */

/**
 * IDENTICAL OPTIONS ON BOTH SURFACES (founder, 2026-09-10: *"I want you to make sure Portal and
 * Mobile have identical options, except for skipping"*).
 *
 * The first build of this editor offered only frequency, weekday chips and a time — mobile's picker
 * also has "repeat every N", "day of the month", "starting", and an Ends group (Never / after N
 * times / on a date), so the portal could not express most of what a client could already have.
 * Rather than write those again, the mapping moved into `@cc/domain/reminders/picker` and BOTH
 * surfaces import it: identical is now a property of the code, not a promise kept by review. The
 * one deliberate difference the founder named is SKIPPING a single occurrence, which stays the
 * client's own act.
 */

export type EsCardRemindersProps = {
  /** The CLIENT — never the therapist. Absent ⇒ the section does not render. */
  subject?: string;
  /** `thoughts/33210` or a bare key; the server bares it (FHIR-2). */
  targetRef?: string;
  targetKind?: string;
};

export const EsCardReminders = ({
  subject,
  targetRef,
  targetKind = 'thoughtTest',
}: EsCardRemindersProps) => {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<ReminderScheduleDraft>(() =>
    defaultDraft(new Date()),
  );
  const patch = (over: Partial<ReminderScheduleDraft>) =>
    setDraft((d) => ({ ...d, ...over }));

  /**
   * The rule as a SENTENCE, through the SAME i18n keys mobile uses (`reminders.summary.*`). The
   * DOMAIN answers which key and which values, so a therapist and their client read the identical
   * description of the identical rule — and Russian agreement stays where a translator can see it.
   */
  const describe = useCallback(
    (schedule: {
      rrule: string;
      dtstart: string;
      tzid?: string | null;
      exdates?: string[] | null;
    }) => {
      const d = summaryOf(schedule.rrule, schedule.dtstart);
      /**
       * The DAYS ARE LOCALISED before the sentence is built. `summaryOf` is pure and returns the
       * RRULE's own codes ("TU,TH"), which is right for a module that must not know about i18n —
       * but interpolating them raw put "Every 2 weeks on TU, TH at 10:00" in front of a therapist
       * (spotted on screen, 2026-09-10). The keys are the same ones the weekday chips use, so the
       * summary and the editor always name a day the same way.
       */
      const values: Record<string, unknown> = { ...d.values };
      if (typeof values.days === 'string' && values.days) {
        values.days = (values.days as string)
          .split(',')
          .map((code) => t(`esReminders.day.${code.trim().toLowerCase()}`))
          .join(', ');
      }
      // a one-time rule's day, in the reader's language ("28 Sep 2026" / "28 сент. 2026 г.")
      if (typeof values.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(values.date)) {
        const [y, m, day] = (values.date as string).split('-').map(Number);
        try {
          values.date = new Date(y, m - 1, day, 12).toLocaleDateString(
            i18n.languages[0],
            { day: 'numeric', month: 'short', year: 'numeric' },
          );
        } catch {
          // keep the ISO day — still a date a person can read
        }
      }
      return t(`reminders.summary.${d.key}`, values);
    },
    [i18n.languages, t],
  );

  const target = useMemo(
    () => ({ kind: targetKind, ref: String(targetRef ?? '') }),
    [targetKind, targetRef],
  );
  const skip = !subject || !targetRef;

  const scheduleFields = ['rrule', 'dtstart', 'tzid', 'exdates'];
  const {
    data,
    isFetching: loading,
    refetch,
  } = useCustom<any>({
    url: '',
    method: 'get',
    meta: {
      operation: 'esCardReminders',
      fields: [
        {
          active: ['reminderId', 'channel', { schedule: scheduleFields }],
        },
        {
          stopped: [
            'reminderId',
            'channel',
            'reason',
            { schedule: scheduleFields },
          ],
        },
        { nextOccurrences: ['reminderId', 'local', 'utcIso'] },
      ],
      variables: {
        target: {
          name: 'target',
          type: 'EsReminderTargetInput!',
          value: target,
        },
        subject: { name: 'subject', type: 'ID', value: subject },
      },
    },
    queryOptions: { enabled: !skip, staleTime: 0, refetchOnMount: 'always' },
  });
  const { mutateAsync: runMutation, isLoading: saving } = useCustomMutation();

  const payload = (data as any)?.data ?? {};
  const active = payload?.active ?? [];
  const stopped = payload?.stopped ?? [];
  const nextByReminder = useMemo(() => {
    const m: Record<string, string> = {};
    (payload?.nextOccurrences ?? []).forEach((o: any) => {
      if (o?.reminderId && o?.local) m[o.reminderId] = o.local;
    });
    return m;
  }, [data]);

  /**
   * A refusal must be SEEN. These mutations reject with a coded message — REMINDER_NOT_CONVERTED
   * for a client the log does not own, REMINDER_SUBJECT_ZONE_UNKNOWN when the client's zone was
   * never captured (R70) — and the copy is already actionable in both languages, so showing the
   * server's own message is better than inventing one.
   */
  /** One place that speaks the dataProvider's `meta` shape, so no call site re-spells it. */
  const setScheduleMutation = useCallback(
    (variables: Record<string, unknown>, operation: string) =>
      runMutation({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: { operation, fields: ['success', 'reminderId'], variables },
      }),
    [runMutation],
  );

  const failed = useCallback(
    (e: unknown) => {
      // the server's text only for a coded refusal; a driver/validation error reads as the translated line
      const msg = graphqlErrorInfo(e).message;
      message.error(msg || t('esReminders.saveFailed'));
    },
    [t],
  );

  /** Open on what the client ACTUALLY has, or on a fresh default for a new reminder. */
  /**
   * The next occurrence as a DATE, not the wire's ISO string. `nextOccurrences[].local` is a local
   * wall clock (`2026-10-09T10:00:00`) because a floating rule has no instant until a zone is
   * assumed — useful to the expander, unreadable in a table cell, where the founder saw it raw.
   * Parsed as LOCAL (never `new Date(...)`, which would treat a bare string as UTC in some engines
   * and shift the hour) and rendered in the therapist's own locale format.
   */
  const formatOccurrence = useCallback(
    (local: string) => {
      try {
        return formatOccurrenceLocal(local, i18n.languages[0]);
      } catch {
        return local;
      }
    },
    [i18n.languages],
  );

  const openEditor = (
    reminderId?: string,
    schedule?: { rrule?: string; dtstart?: string },
  ) => {
    setDraft(
      schedule?.rrule
        ? draftOfSchedule(
            { rrule: schedule.rrule, dtstart: schedule.dtstart ?? '' },
            new Date(),
          )
        : defaultDraft(new Date()),
    );
    setEditing(reminderId ?? null);
  };

  const save = async () => {
    try {
      /**
       * The shared mapper builds the rule AND an anchor that matches it — RFC 5545 requires DTSTART
       * to fall on its own rule and the decider refuses a mismatch, so a weekly or monthly anchor is
       * ADVANCED to the first day the rule can fire rather than offered up to be rejected.
       */
      const problem = draftProblem(draft);
      if (problem) {
        message.error(t(`reminderProblem.${problem}`));
        return;
      }
      const built = scheduleOfDraft(draft);
      const rrule = built.rrule;
      const dtstart = built.dtstart;
      await setScheduleMutation(
        {
          ...(editing
            ? { reminderId: { name: 'reminderId', type: 'ID', value: editing } }
            : {}),
          target: {
            name: 'target',
            type: 'EsReminderTargetInput!',
            value: target,
          },
          // `tzid` is NOT sent: the server resolves the CLIENT's zone for an on-behalf write and
          // refuses when it is unknown (R70). Sending the therapist's zone is the bug that rule stops.
          schedule: {
            name: 'schedule',
            type: 'EsReminderScheduleInput!',
            value: { rrule, dtstart, exdates: [] },
          },
          channel: { name: 'channel', type: 'String', value: 'local' },
          subject: { name: 'subject', type: 'ID', value: subject },
        },
        'setReminderSchedule',
      );
      setEditing(undefined);
      await refetch();
      message.success(t('esReminders.saved'));
    } catch (e) {
      failed(e);
    }
  };

  const end = async (reminderId: string, reason: 'disabled' | 'deleted') => {
    try {
      await setScheduleMutation(
        {
          reminderId: { name: 'reminderId', type: 'ID!', value: reminderId },
          reason: { name: 'reason', type: 'String!', value: reason },
          subject: { name: 'subject', type: 'ID', value: subject },
        },
        'endReminderSchedule',
      );
      await refetch();
    } catch (e) {
      failed(e);
    }
  };

  const revive = async (reminderId: string, rrule: string, dtstart: string) => {
    try {
      // there is no re-enable verb: turning a stopped row back on is a fresh Set of the rule the
      // log still holds, which is exactly what the decider's "a resend after an END revives it" means
      await setScheduleMutation(
        {
          reminderId: { name: 'reminderId', type: 'ID', value: reminderId },
          target: {
            name: 'target',
            type: 'EsReminderTargetInput!',
            value: target,
          },
          schedule: {
            name: 'schedule',
            type: 'EsReminderScheduleInput!',
            value: { rrule, dtstart, exdates: [] },
          },
          channel: { name: 'channel', type: 'String', value: 'local' },
          subject: { name: 'subject', type: 'ID', value: subject },
        },
        'setReminderSchedule',
      );
      await refetch();
    } catch (e) {
      failed(e);
    }
  };

  if (skip) return null;

  return (
    <div data-testid="cc-es-reminders">
      <Typography.Text strong>{t('esReminders.title')}</Typography.Text>
      {/*
        A LIST, NOT A NESTED TABLE (founder, portal testing 2026-09-10 — screenshot).
        This cell lives inside the card table's 300px "Time-based Reminders" column. A nested antd
        Table split that into three ~100px columns, so "Every 2 months at 10:00" rendered ONE
        CHARACTER PER LINE. A table inside a narrow cell is the wrong instrument: these are three
        facts about one reminder, not a grid, so they stack and the row is free to be as wide as the
        column allows.
      */}
      <div data-testid="cc-es-reminders-active">
        {loading && active.length === 0 ? (
          <Typography.Text type="secondary">
            {t('esReminders.loading')}
          </Typography.Text>
        ) : active.length === 0 ? (
          <Typography.Text type="secondary">
            {t('esReminders.empty')}
          </Typography.Text>
        ) : (
          active.map((r: any) => (
            <div
              key={r.reminderId}
              style={{
                padding: '8px 0',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <div>
                <Typography.Text strong>
                  {describe(r?.schedule ?? { rrule: '', dtstart: '' })}
                </Typography.Text>
              </div>
              {nextByReminder[r.reminderId] && (
                <div>
                  <Typography.Text type="secondary">
                    {t('esReminders.next', {
                      when: formatOccurrence(nextByReminder[r.reminderId]),
                    })}
                  </Typography.Text>
                </div>
              )}
              <Space size={4} wrap>
                <Button
                  type="link"
                  size="small"
                  style={{ paddingInline: 0 }}
                  icon={<EditOutlined />}
                  data-testid={`cc-es-reminder-edit-${r.reminderId}`}
                  onClick={() => openEditor(r.reminderId, r?.schedule)}
                >
                  {t('common.edit')}
                </Button>
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  data-testid={`cc-es-reminder-off-${r.reminderId}`}
                  onClick={() => end(r.reminderId, 'disabled')}
                >
                  {t('esReminders.turnOff')}
                </Button>
              </Space>
            </div>
          ))
        )}
      </div>
      <Button
        icon={<PlusOutlined />}
        data-testid="cc-es-reminder-add"
        style={{ marginTop: 12 }}
        onClick={() => openEditor()}
      >
        {t('esReminders.add')}
      </Button>

      {stopped.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Typography.Text strong>
            {t('esReminders.stoppedTitle')}
          </Typography.Text>
          <div data-testid="cc-es-reminders-stopped">
            {stopped.map((r: any) => (
              <div
                key={r.reminderId}
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <div>
                  <Typography.Text strong>
                    {describe(r?.schedule ?? { rrule: '', dtstart: '' })}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    {t(`esReminders.reason.${r?.reason ?? 'disabled'}`)}
                  </Typography.Text>
                </div>
                <Button
                  type="link"
                  size="small"
                  style={{ paddingInline: 0 }}
                  data-testid={`cc-es-reminder-revive-${r.reminderId}`}
                  onClick={() =>
                    revive(
                      r.reminderId,
                      r?.schedule?.rrule ?? '',
                      r?.schedule?.dtstart ?? '',
                    )
                  }
                >
                  {t('esReminders.turnBackOn')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={editing !== undefined}
        title={t('esReminders.editorTitle')}
        okText={t('esReminders.save')}
        cancelText={t('common.cancel')}
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(undefined)}
        data-testid="cc-es-reminder-editor"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* HOW OFTEN — the four v1 frequencies, same set and same order as the mobile picker */}
          <div>
            <Typography.Text>{t('esReminders.repeat')}</Typography.Text>
            <Select
              data-testid="cc-es-reminder-repeat"
              style={{ width: '100%' }}
              value={draft.frequency}
              onChange={(frequency) => patch({ frequency })}
              options={[
                { value: 'daily', label: t('esReminders.repeatDaily') },
                { value: 'weekly', label: t('esReminders.repeatWeekly') },
                { value: 'monthly', label: t('esReminders.repeatMonthly') },
                { value: 'oneTime', label: t('esReminders.repeatOnce') },
              ]}
            />
          </div>

          {/* REPEAT EVERY N — INTERVAL. Meaningless for a one-time rule, which is COUNT=1. */}
          {draft.frequency !== 'oneTime' && (
            <div>
              <Typography.Text>{t('esReminders.every')}</Typography.Text>
              <InputNumber
                data-testid="cc-es-reminder-interval"
                style={{ width: '100%' }}
                min={1}
                max={99}
                value={draft.interval}
                onChange={(v) => patch({ interval: Number(v) || 1 })}
                addonAfter={t(`esReminders.unit.${draft.frequency}`)}
              />
            </div>
          )}

          {draft.frequency === 'weekly' && (
            <div>
              <Typography.Text>{t('esReminders.onDays')}</Typography.Text>
              <Checkbox.Group
                data-testid="cc-es-reminder-days"
                style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                value={draft.weekdays}
                onChange={(v) => patch({ weekdays: v as ReminderWeekday[] })}
                options={WEEKDAYS.map((d: ReminderWeekday) => ({
                  value: d,
                  label: t(`esReminders.day.${d.toLowerCase()}`),
                }))}
              />
            </div>
          )}

          {/* DAY OF THE MONTH — 1..31, the field the portal was missing entirely */}
          {draft.frequency === 'monthly' && (
            <div>
              <Typography.Text>{t('esReminders.monthDay')}</Typography.Text>
              <InputNumber
                data-testid="cc-es-reminder-monthday"
                style={{ width: '100%' }}
                min={1}
                max={31}
                value={draft.monthDay}
                onChange={(v) => patch({ monthDay: Number(v) || 1 })}
              />
            </div>
          )}

          <div>
            <Typography.Text>{t('esReminders.at')}</Typography.Text>
            <DateFnsTimePicker
              data-testid="cc-es-reminder-time"
              style={{ width: '100%' }}
              format={i18n.languages[0]?.startsWith('ru') ? 'HH:mm' : 'hh:mm a'}
              minuteStep={5}
              allowClear={false}
              value={setTime(new Date(), {
                hours: draft.hour,
                minutes: draft.minute,
              })}
              onChange={(date: Date | Date[]) => {
                if (Array.isArray(date) || !date) return;
                patch({ hour: date.getHours(), minute: date.getMinutes() });
              }}
            />
          </div>

          {/* STARTING — the first day the rule may fire; the anchor DTSTART is built from */}
          <div>
            <Typography.Text>{t('esReminders.starting')}</Typography.Text>
            <DateFnsDatePicker
              data-testid="cc-es-reminder-start"
              style={{ width: '100%' }}
              allowClear={false}
              value={
                new Date(
                  draft.startDate.year,
                  draft.startDate.month - 1,
                  draft.startDate.day,
                )
              }
              onChange={(date: Date | Date[]) => {
                if (Array.isArray(date) || !date) return;
                patch({
                  startDate: {
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                    day: date.getDate(),
                  },
                });
              }}
            />
          </div>

          {/* ENDS — Never / after N times (COUNT) / on a date (UNTIL). RFC 5545 §3.3.10 makes COUNT
              and UNTIL mutually exclusive, which is why this is one choice and not two fields. */}
          {draft.frequency !== 'oneTime' && (
            <div>
              <Typography.Text>{t('esReminders.ends')}</Typography.Text>
              <Radio.Group
                data-testid="cc-es-reminder-ends"
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                value={draft.bound}
                onChange={(e) => patch({ bound: e.target.value })}
              >
                <Radio value="never">{t('esReminders.endsNever')}</Radio>
                <Radio value="count">
                  <Space>
                    {t('esReminders.endsAfter')}
                    <InputNumber
                      data-testid="cc-es-reminder-count"
                      min={1}
                      max={999}
                      disabled={draft.bound !== 'count'}
                      value={draft.count}
                      onChange={(v) => patch({ count: Number(v) || 1 })}
                    />
                    {t('esReminders.endsTimes')}
                  </Space>
                </Radio>
                <Radio value="until">
                  <Space>
                    {t('esReminders.endsOn')}
                    <DateFnsDatePicker
                      data-testid="cc-es-reminder-until"
                      allowClear={false}
                      disabled={draft.bound !== 'until'}
                      value={
                        new Date(
                          draft.untilDate.year,
                          draft.untilDate.month - 1,
                          draft.untilDate.day,
                        )
                      }
                      onChange={(date: Date | Date[]) => {
                        if (Array.isArray(date) || !date) return;
                        patch({
                          untilDate: {
                            year: date.getFullYear(),
                            month: date.getMonth() + 1,
                            day: date.getDate(),
                          },
                        });
                      }}
                    />
                  </Space>
                </Radio>
              </Radio.Group>
            </div>
          )}

          {/* the zone is the CLIENT's and the server supplies it — saying so is the honest UI for a
              rule that refuses to be written without one (R70) */}
          <Typography.Text type="secondary">
            {t('esReminders.clientZoneNote')}
          </Typography.Text>
        </Space>
      </Modal>
    </div>
  );
};

export default EsCardReminders;

/**
 * ONE ROW'S REMINDER CELL, era-aware.
 *
 * A list can hold cards belonging to different clients, and the era is per CLIENT — so the decision
 * cannot be made once for the table. A cell component can ask the hook for its own row, which a
 * `render` callback cannot. Legacy clients keep the shipped 3-slot table verbatim; a converted
 * client gets the RRULE surface, which is the only one that can show a weekly rule honestly or edit
 * it without flattening it (R71).
 */
export const CardRemindersCell = ({
  subject,
  targetRef,
  legacy,
}: {
  subject?: string;
  targetRef?: string;
  legacy: React.ReactNode;
}) => {
  const { isEsClient, eraLoaded } = useClientEra(subject, 'reminders');
  // until the era answers, show the shipped surface rather than flashing an empty ES one
  if (!eraLoaded || !isEsClient) return <>{legacy}</>;
  return <EsCardReminders subject={subject} targetRef={targetRef} />;
};
